import { useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Download, Eye, LoaderCircle, MoreVertical, Paperclip } from 'lucide-react';
import { ActionSheet } from '@shared/ui/containers/ActionSheet';
import { toast } from '@shared/ui/feedback/Toast';
import { fetchAttachmentDownload } from './api';
import type { FeedAttachment } from './types';

export function formatAttachmentSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Reads `metadata.attachments` off a notification defensively — the column is freeform
 * jsonb shared with other notification producers, and messages sent before attachments
 * existed have none.
 */
export function readAttachments(metadata: unknown): FeedAttachment[] {
  const list = (metadata as { attachments?: unknown } | null)?.attachments;
  if (!Array.isArray(list)) return [];
  return list
    .filter((a): a is Record<string, unknown> => Boolean(a) && typeof a === 'object')
    .filter((a) => typeof a.document_id === 'string' && (a.document_id as string).length > 0)
    .map((a) => ({
      document_id: a.document_id as string,
      filename: typeof a.filename === 'string' && a.filename ? a.filename : 'Attachment',
      mime_type: typeof a.mime_type === 'string' ? a.mime_type : null,
      size_bytes: typeof a.size_bytes === 'number' ? a.size_bytes : null,
    }));
}

/**
 * Files attached to a message.
 *
 * No row here is a link to a document. Each one asks the server for a fresh, short-lived
 * URL at the moment it is tapped, and the server only mints one after confirming this
 * message belongs to this resident and genuinely carries that file — so a resident can
 * never reach a document that was not sent to them.
 */
export function AttachmentList({
  tenantId,
  notificationId,
  attachments,
}: {
  tenantId: string | null;
  notificationId: string;
  attachments: FeedAttachment[];
}): JSX.Element | null {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<FeedAttachment | null>(null);
  if (attachments.length === 0) return null;

  // 'inline' renders the file in place of saving it — the two actions differ only in the
  // Content-Disposition the tenant signs the URL with.
  const open = async (a: FeedAttachment, disposition: 'inline' | 'attachment') => {
    setBusyId(a.document_id);
    try {
      const { download_url } = await fetchAttachmentDownload(tenantId, notificationId, a.document_id, disposition);
      await openExternalUrl(download_url);
    } catch {
      toast.danger("Couldn't open that attachment", 'Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-hairline bg-card p-3">
      <p className="text-caption font-semibold uppercase tracking-wide text-ink-muted">
        {attachments.length === 1 ? 'Attachment' : `Attachments (${attachments.length})`}
      </p>
      <ul className="mt-2 flex flex-col gap-1">
        {attachments.map((a) => (
          <li key={a.document_id}>
            <button
              type="button"
              onClick={() => setMenuFor(a)}
              disabled={busyId === a.document_id}
              aria-label={`Actions for ${a.filename}`}
              className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-body-sm text-ink hover:bg-sunken disabled:opacity-60"
            >
              {busyId === a.document_id ? (
                <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-primary-700" strokeWidth={1.75} />
              ) : (
                <Paperclip className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
              )}
              <span className="min-w-0 flex-1 truncate">{a.filename}</span>
              {formatAttachmentSize(a.size_bytes) ? (
                <span className="shrink-0 text-caption text-ink-muted">{formatAttachmentSize(a.size_bytes)}</span>
              ) : null}
              <MoreVertical className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
            </button>
          </li>
        ))}
      </ul>

      <ActionSheet
        open={!!menuFor}
        onOpenChange={(isOpen) => { if (!isOpen) setMenuFor(null); }}
        title={menuFor?.filename}
        actions={[
          {
            label: 'View',
            leading: <Eye className="h-4 w-4" strokeWidth={1.75} />,
            onSelect: () => { if (menuFor) void open(menuFor, 'inline'); },
          },
          {
            label: 'Download',
            leading: <Download className="h-4 w-4" strokeWidth={1.75} />,
            onSelect: () => { if (menuFor) void open(menuFor, 'attachment'); },
          },
        ]}
      />
    </div>
  );
}

/**
 * `window.open` is a no-op inside the native WebView, which is why tapping an attachment
 * appeared to do nothing. Mirrors the app's other external links.
 */
async function openExternalUrl(url: string): Promise<void> {
  await Browser.open({ url, presentationStyle: 'popover' }).catch(() => {
    window.open(url, '_blank', 'noopener,noreferrer');
  });
}
